/* SIPOC Mapper — Suppliers · Inputs · Process · Outputs · Customers */
import ToolWizard from "./ToolWizard";
import { Plus, Trash2, FileText } from "lucide-react";

const COLS = [
  { key: "suppliers", label: "Suppliers", color: "#6366F1", help: "Who provides the inputs?" },
  { key: "inputs",    label: "Inputs",    color: "#0EA5E9", help: "What is supplied?" },
  { key: "process",   label: "Process",   color: "#7C9A7E", help: "Top-level steps (5–7)" },
  { key: "outputs",   label: "Outputs",   color: "#D97706", help: "What is produced?" },
  { key: "customers", label: "Customers", color: "#DC2626", help: "Who receives outputs?" },
];

function ColumnEditor({ data, updateData, col }) {
  const items = data[col.key] || [];
  const add = () => updateData({ [col.key]: [...items, { text: "", note: "" }] });
  const upd = (i, p) => updateData({ [col.key]: items.map((m, idx) => idx === i ? { ...m, ...p } : m) });
  const rm  = (i) => updateData({ [col.key]: items.filter((_, idx) => idx !== i) });
  return (
    <div className="bg-white border-2 rounded-md overflow-hidden" style={{ borderColor: col.color }} data-testid={`sipoc-col-${col.key}`}>
      <div className="px-3 py-2 text-white text-xs font-bold uppercase tracking-wider" style={{ backgroundColor: col.color }}>{col.label}</div>
      <div className="p-2 space-y-1.5 min-h-[150px]">
        {items.map((it, i) => (
          <div key={i} className="bg-sage-50 rounded-md p-2 space-y-1">
            <div className="flex gap-1">
              <input data-testid={`sipoc-${col.key}-${i}-text`} placeholder={col.help} value={it.text} onChange={e => upd(i, { text: e.target.value })} className="flex-1 border border-sage-200 rounded-md px-2 py-1.5 text-xs bg-white" />
              <button onClick={() => rm(i)} className="text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
            <input placeholder="Note (optional)" value={it.note || ""} onChange={e => upd(i, { note: e.target.value })} className="w-full border border-sage-100 rounded-md px-2 py-1 text-[11px] bg-white text-sage-600" />
          </div>
        ))}
        <button data-testid={`sipoc-${col.key}-add`} onClick={add} className="flex items-center gap-1 border border-dashed border-sage-300 text-sage-600 rounded-md px-2 py-1.5 text-xs hover:bg-sage-50 w-full justify-center"><Plus className="w-3.5 h-3.5" /> Add {col.label.slice(0, -1)}</button>
      </div>
    </div>
  );
}

export default function SipocWizard({ project, onSave, onExportPDF }) {
  const steps = [
    {
      key: "scope", label: "Scope",
      render: ({ data, updateData }) => (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="sipoc-scope">
          <div className="md:col-span-2"><label className="block text-xs font-semibold text-sage-600 mb-1">Process / value-stream name</label><input data-testid="sipoc-process-name" value={data.process_name || ""} onChange={e => updateData({ process_name: e.target.value })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm" placeholder="e.g. New Customer Onboarding" /></div>
          <div><label className="block text-xs font-semibold text-sage-600 mb-1">Process owner</label><input value={data.owner || ""} onChange={e => updateData({ owner: e.target.value })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm" /></div>
          <div><label className="block text-xs font-semibold text-sage-600 mb-1">Boundary start → end</label><input value={data.boundary || ""} onChange={e => updateData({ boundary: e.target.value })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm" placeholder="e.g. Quote requested → Service activated" /></div>
          <div className="md:col-span-2"><label className="block text-xs font-semibold text-sage-600 mb-1">Goal of the SIPOC</label><textarea value={data.goal || ""} onChange={e => updateData({ goal: e.target.value })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm min-h-[70px]" /></div>
        </div>
      ),
    },
    {
      key: "canvas", label: "Canvas",
      render: ({ data, updateData }) => (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2" data-testid="sipoc-canvas">
          {COLS.map(c => <ColumnEditor key={c.key} data={data} updateData={updateData} col={c} />)}
        </div>
      ),
    },
    {
      key: "diagnostic", label: "Diagnostic",
      render: ({ data }) => {
        const counts = COLS.map(c => ({ label: c.label, color: c.color, n: (data[c.key] || []).length }));
        const balanced = counts.every(c => c.n > 0);
        return (
          <div className="space-y-4" data-testid="sipoc-diagnostic">
            <div className={`rounded-md p-3 text-sm ${balanced ? "bg-green-50 border border-green-200 text-green-800" : "bg-amber-50 border border-amber-200 text-amber-800"}`}>
              {balanced ? "All five columns are populated — SIPOC is complete." : "Add at least one item in every column to complete SIPOC."}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {counts.map(c => (
                <div key={c.label} className="rounded-md p-3 border" style={{ borderColor: c.color }}>
                  <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: c.color }}>{c.label}</p>
                  <p className="font-heading text-2xl font-bold text-sage-900">{c.n}</p>
                </div>
              ))}
            </div>
            <button data-testid="export-sipoc-pdf" onClick={() => onExportPDF(data)} className="bg-sage-600 text-white rounded-md px-4 py-2 text-sm font-semibold hover:bg-sage-700 flex items-center gap-2"><FileText className="w-4 h-4" /> Export PDF</button>
          </div>
        );
      },
    },
  ];
  return <ToolWizard project={project} steps={steps} onSave={onSave} completedLabel="Finish SIPOC" />;
}

export const SIPOC_COLS = COLS;
